import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import * as Sentry from "@sentry/angular";
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ContainerComponent } from './container/container.component';
import { NotificationsComponent } from './container/notifications/notifications.component';
import { LoadingSpacePanelModule } from './container/overlaps-loading-spaces/loading-space-panel/loading-space-panel.module';
import { LoadingSpacesSummaryComponent } from './container/overlaps-loading-spaces/loading-spaces-summary/loading-spaces-summary.component';
import { OverlapsLoadingSpacesComponent } from './container/overlaps-loading-spaces/overlaps-loading-spaces.component';
import { PaymentApiSuccessPopupComponent } from './container/payment-api-success-popup/payment-api-success-popup.component';
import { PaymentSuccessPopupComponent } from './container/payment-success-popup/payment-success-popup.component';
import { ProlongPopupComponent } from './container/prolong-popup/prolong-popup.component';
import { RecommendationsComponent } from './container/recommendations/recommendations.component';
import { RolesEmailPopupComponent } from './container/roles-email-popup/roles-email-popup.component';
import { SlotsPopupComponent } from './container/slots-popup/slots-popup.component';
import { WelcomeApiPopupComponent } from './container/welcome-api-popup/welcome-api-popup.component';
import { WelcomeProPopupComponent } from './container/welcome-pro-popup/welcome-pro-popup.component';
import { CoreModule } from './core/core.module';
import { ErrorInterceptor } from './core/interceptors/error.service';
import { LoaderInterceptor } from './core/interceptors/loader.service';
import { TokenInterceptor } from './core/interceptors/token.service';
import { AppConfigService } from './core/services/config.service';
import { LanguageService } from './core/services/language.service';
import { SentryUserContextService } from './core/services/sentry-user-context.service';
import { EditMenuComponent } from './edit-menu/edit-menu.component';
import { MainPanelModule } from './main-panel/main-panel.module';
import { MobileMenuComponent } from './mobile-menu/mobile-menu.component';
import { ModalModule } from './modal/modal.module';
import { NewsItemComponent } from './news/news-item/news-item.component';
import { NewsComponent } from './news/news.component';
import { SharedModule } from './shared/shared.module';
import { WorkingTableModule } from './working-table/working-table.module';
import { AboutMyAccountPopupComponent } from './container/about-my-account-popup/about-my-account-popup.component';
import { TopMenuModule } from './top-menu/top-menu.module';
import { MainContainerComponent } from './main-container/main-container.component';
import { ContestPopupComponent } from './container/contest-popup/contest-popup.component';
import { DataImportPopupComponent } from './container/data-import-popup/data-import-popup.component';
import { UpdatesPopupComponent } from './container/updates-popup/updates-popup.component';
import {
  ExternalUserPasswordPopupComponent
} from './container/external-user-password-popup/external-user-password-popup.component';
import {
  CompleteLocationDataPopupComponent
} from './container/complete-location-data/complete-location-data-popup.component';
import { SharedChangesPopupComponent } from './container/shared-changes-popup/shared-changes-popup.component';
import {
  SharedChangesEditPopupComponent
} from './container/shared-changes-edit-popup/shared-changes-edit-popup.component';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha-2';
import { SendToTransComponent } from './top-menu/send-to-trans/send-to-trans.component';
import { TransFormComponent } from './top-menu/trans-form/trans-form.component';
import { VideoPopupComponent } from './container/video-popup/video-popup.component';
import { CustomerResearchPopupComponent } from './container/customer-research-popup/customer-research-popup.component';
import { Router } from '@angular/router';
function loadConfigService(configService: AppConfigService) {
  return () => configService.load();
}
@NgModule({ declarations: [
        AppComponent,
        ContainerComponent,
        RolesEmailPopupComponent,
        NotificationsComponent,
        RecommendationsComponent,
        LoadingSpacesSummaryComponent,
        OverlapsLoadingSpacesComponent,
        PaymentSuccessPopupComponent,
        SlotsPopupComponent,
        WelcomeProPopupComponent,
        VideoPopupComponent,
        CustomerResearchPopupComponent,
        ProlongPopupComponent,
        MobileMenuComponent,
        UpdatesPopupComponent,
        WelcomeApiPopupComponent,
        PaymentApiSuccessPopupComponent,
        NewsComponent,
        NewsItemComponent,
        EditMenuComponent,
        AboutMyAccountPopupComponent,
        MainContainerComponent,
        ContestPopupComponent,
        DataImportPopupComponent,
        ExternalUserPasswordPopupComponent,
        CompleteLocationDataPopupComponent,
        SharedChangesPopupComponent,
        SharedChangesEditPopupComponent,
        SendToTransComponent,
        TransFormComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        CoreModule,
        RecaptchaV3Module,
        TranslateModule.forRoot(),
        AppRoutingModule,
        SharedModule,
        FormsModule,
        ReactiveFormsModule,
        MainPanelModule,
        WorkingTableModule,
        LoadingSpacePanelModule,
        DragDropModule,
        ModalModule,
        TopMenuModule], providers: [
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: '6LduiNspAAAAABMLMltcP1Il27Cl4oNil5SAzyum' },
        provideHttpClient(),
        provideTranslateHttpLoader({
            prefix: '/translations/',
            suffix: '.json'
        }),
        {
          provide: ErrorHandler,
          useValue: Sentry.createErrorHandler({
            showDialog: false,
          }),
        },         {
          provide: Sentry.TraceService,
          deps: [Router],
        },
        provideAppInitializer(() => {
          inject(Sentry.TraceService);
          inject(SentryUserContextService);
        }),
        AppConfigService,
        provideAppInitializer(() => {
          const initializerFn = loadConfigService(inject(AppConfigService));
          return initializerFn();
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LoaderInterceptor,
            multi: true,
        },
        LanguageService,
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
